// react core
import { useContext, useState, useEffect } from "react";
import { useLocation } from "react-router";

// entzy config and components
import configEntzy from "components/config/ConfigEntzy";

// material design
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// fonts
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStripe as iconIncome } from "@fortawesome/free-brands-svg-icons";
import { faCog as iconOtherActions } from "@fortawesome/pro-thin-svg-icons";
import { faArrowRight as iconArrowRight } from "@fortawesome/pro-duotone-svg-icons";

// entzy context and services
import InputForm from "components/input/InputForm";
import { SettingsContext } from "components/settings/SettingsContext";
import { ActiveTag, WarningTag } from "components/utils/common/CommonTags";
import {
  ActionAlert,
  ActionLoader,
  ActionCheck,
} from "components/utils/common/CommonLoaders";
import { ActionBoxButton } from "components/utils/common/CommonButtons";

// user hooks
import { userGet } from "hooks/identity";

// alert messages
const ALERT_MESSAGES = {
  errors: {
    accountLogin:
      "Currently unable to generate a login connection. Give it another go or contact us if this persists.",
    accountActivateCode:
      "A valid account connection code is required (sent by Stripe on completion of account creation)",
  },
  success: {
    accountLogin:
      "Your account login url is ready. Click the link to connect to your income account.",
    accountDeactivate: "Your income account was successfully deactivated",
  },
};

function IncomeBank(props) {
  const context = useContext(SettingsContext);
  const location = useLocation();
  const navigate = props.navigate;
  const page = props.page;
  const userReload = props.userReload;
  const isValidate = location.pathname.endsWith("/validate");

  const [loading, setLoading] = useState(false);
  const [gotLatestUserStatus, setGotLatestUserStatus] = useState(false);
  const [linkInProgress, setLinkInProgress] = useState(false);
  const [activating, setActivating] = useState(false);
  const [otherActions, setOtherActions] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [alert, setAlert] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  // const [createCode, setCreateCode] = useState(props.searchParams.get("code"));

  // component functions
  const handleOpenAccountActivate = async (url) => {
    setLoading(true);
    setLinkInProgress(true);
    props.menuSelectById(
      "connecting:StripeAccountSetup_" + url.replace("https://", ""),
    );
    return {
      alert: false,
    };
  };
  const handleRefresh = async () => {
    setLoading(true);
    window.location.reload();
  };

  const handleOtherActions = async () => {
    setOtherActions(!otherActions);
  };

  const handleAccountSetup = async (data) => {
    const accountCountry = data.filter((obj) => obj.id === "account-country")[0]
      .value;
    if (!accountCountry) {
      return {
        alert: true,
        message: "Please select your bank account country to proceed.",
      };
    }
    const country = accountCountry.code.short;
    setLoading(true);
    setAlert(false);
    setSuccess(false);
    setMessage("");
    const config = {};
    if (configEntzy.IS_LOCAL) {
      config.local = true;
    }
    const userData = await context.prepareIncomeAccountSetup({
      setup: true,
      country: country,
      config: config,
    });
    setLoading(false);
    if (userData.alert) {
      return {
        alert: true,
        message: userData.message,
      };
    } else {
      return await handleOpenAccountActivate(userData.url);
    }
  };

  const handleAccountActivate = async () => {
    const currentPayoutId = context.state.settings.data.PayoutId;
    const currentAccountId = currentPayoutId
      ? currentPayoutId.split(":")[0]
      : null;
    if (!currentAccountId) {
      return {
        alert: true,
        message:
          "No account linking activity found to validate. Tap the Stripe button to initiate the linking process.",
      };
    }
    const config = {
      account: currentAccountId,
    };
    if (configEntzy.IS_LOCAL) {
      config.local = true;
    }
    const userData = await context.prepareIncomeAccountSetup({
      validate: true,
      country: "validate",
      config: config,
    });
    if (userData.alert) {
      setMessage(userData.message);
      setAlert(true);
      return {
        alert: true,
        message: userData.message,
      };
    }
    const settingsUpdate = await context.prepareUpdateSettings({
      key: "PayoutId",
      value: userData.PayoutId,
    });
    if (settingsUpdate.alert) {
      return {
        alert: true,
        message: settingsUpdate.message,
      };
    } else {
      context.updateSettings(settingsUpdate);
      userReload();
      return {
        alert: false,
        message: ALERT_MESSAGES.success.accountActivate,
      };
    }
  };

  const handleAccountDeactivateCancel = async () => {
    setDialog(false);
  };
  const handleAccountDeactivateConfirm = async () => {
    handleAccountDeactivate(null, true);
  };

  const handleAccountDeactivate = async (event, confirm) => {
    // TODO: VALIDATE THAT NO PAID EVENTS IN PROGRESS
    // HANDLE THIS BY CHECKING EVENTS OR DO WE HAVE A FALLBACK SUSPENSE ACCOUNT?
    if (confirm) {
      setDialog(false);
      setLoading(true);
      const settingsUpdate = await context.prepareUpdateSettings({
        key: "PayoutId",
        value: "delete",
      });
      if (settingsUpdate.alert) {
        setMessage(settingsUpdate.message);
        setAlert(true);
      } else {
        context.updateSettings(settingsUpdate);
        setMessage(ALERT_MESSAGES.success.accountDeactivate);
        setSuccess(true);
        userReload();
      }
      setLoading(false);
    } else {
      setDialog(true);
    }
  };

  // hook: first time account activation callback
  useEffect(() => {
    const activateIncomeBank = async () => {
      setActivating(true);
      setLoading(true);
      const results = await handleAccountActivate();
      setMessage(results.message);
      setAlert(results.alert);
      setLoading(false);
      // setCreateCode(null);
      setActivating(false);
      navigate(page);
    };
    // if (createCode && !activating) {
    //   activateIncomeBank();
    // }
    if (isValidate && !activating) {
      activateIncomeBank();
    }
  });

  // hook: always get latest user status on load
  // in case flow was completed in another tab or window
  useEffect(() => {
    const getLatestUserStatus = async () => {
      const latestUser = await userGet();
      const latestUserPayoutSet = latestUser.status
        ? latestUser.status.payout_set
        : null;
      if (latestUserPayoutSet !== context.state.user.status.payout_set) {
        userReload();
      }
      setGotLatestUserStatus(true);
    };
    if (!gotLatestUserStatus) {
      getLatestUserStatus();
    }
  }, [gotLatestUserStatus, userReload, context.state.user.status.payout_set]);

  return (
    <Box
      className="box-default"
      sx={{
        pt: configEntzy.APP_SPACING_LG,
        pb: configEntzy.APP_SPACING_LG,
      }}
    >
      <Container maxWidth="sm">
        <Box className="box-default" hidden={loading || linkInProgress}>
          <Box className="box-default">
            <ActiveTag
              value={context.state.user.status.payout_set}
              prefix="income account"
            />
          </Box>
          {context.state.user.status.payout_set &&
            context.state.settings.data.PayoutId && (
              <Box
                className="box-default"
                sx={{
                  mt: configEntzy.APP_SPACING_LG,
                }}
              >
                {context.state.settings.data.PayoutId.split(":")[0] ===
                "none" ? (
                  <WarningTag text="Income account link could not be detected. Refreshing or reconnecting may resolve but if the problem persists try unlinking and relinking your account." />
                ) : (
                  <Box className="box-default">
                    <Typography variant="subtitle2" noWrap={true}>
                      Your income account is connected
                    </Typography>
                    <Typography variant="subtitle2" noWrap={true}>
                      You have selling capability
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          <Box className="box-default">
            {context.state.user.status.payout_set ? (
              <Box className="box-default">
                <Box
                  className="box-default"
                  sx={{
                    mt: configEntzy.APP_SPACING_LG,
                  }}
                >
                  <ActionBoxButton
                    variant="contained"
                    bgColor="black"
                    color="white"
                    borderColor="purple"
                    size="medium"
                    onClick={() =>
                      props.menuSelectById("settings-income-balance")
                    }
                    text="Go to Account Balance"
                    topIcon={
                      <FontAwesomeIcon
                        icon={iconIncome}
                        size="3x"
                        transform="up-2"
                      />
                    }
                    endIcon={<FontAwesomeIcon icon={iconArrowRight} />}
                  />
                </Box>
                <Container maxWidth="xs">
                  <Box
                    className="box-default"
                    sx={{
                      mt: configEntzy.APP_SPACING_HL,
                      pt: configEntzy.APP_SPACING_HL,
                    }}
                  >
                    <ActionBoxButton
                      variant="contained"
                      bgColor="black"
                      color="white"
                      borderColor="#333"
                      size="small"
                      onClick={handleOtherActions}
                      text={otherActions ? "Cancel X" : "Other Actions"}
                      endIcon={
                        otherActions ? null : (
                          <FontAwesomeIcon icon={iconOtherActions} />
                        )
                      }
                    />
                  </Box>
                  <Box
                    className="box-default"
                    sx={{
                      mt: configEntzy.APP_SPACING_MD,
                    }}
                    hidden={!otherActions}
                  >
                    <ActionBoxButton
                      variant="outlined"
                      size="small"
                      bgColor="danger.dark"
                      color="danger.contrastText"
                      onClick={handleAccountDeactivate}
                      sx={{ opacity: 0.8 }}
                      text="Unlink Account"
                      subtext="Remove the income bank link"
                    />
                  </Box>
                  <Box className="box-default">
                    <ActionCheck
                      dialog={dialog}
                      title="Confirm Unlink Income Account"
                      description="Warning! Please ensure you have no remaining balance left in your account as it will no longer be visible after you unlink it. Select yes to proceed with the disconnection."
                      cancel={handleAccountDeactivateCancel}
                      confirm={handleAccountDeactivateConfirm}
                    />
                  </Box>
                </Container>
              </Box>
            ) : (
              <Box
                className="box-default"
                sx={{
                  mt: configEntzy.APP_SPACING_XL,
                }}
              >
                <Box className="box-default">
                  <Box
                    className="box-default"
                    sx={{
                      pb: configEntzy.APP_SPACING_LG,
                    }}
                  >
                    <Typography variant="h6">
                      <FontAwesomeIcon
                        icon={iconIncome}
                        size="3x"
                        transform="up-2"
                      />
                    </Typography>
                    <Typography variant="subtitle1">
                      To start accepting payments link your bank account
                    </Typography>
                    <Typography variant="subtitle1">
                      Entzy uses Stripe for payment processing
                    </Typography>
                    <Typography variant="subtitle1" color="primary">
                      Confirm your country to proceed
                    </Typography>
                  </Box>
                  <Container maxWidth="xs">
                    <InputForm
                      navigate={props.navigate}
                      fields={[
                        {
                          id: "account-country",
                          type: "selector-country",
                          label: "Select your bank account country",
                          value: null,
                          required: true,
                        },
                      ]}
                      submitText="Continue to Stripe"
                      callback={handleAccountSetup}
                    />
                  </Container>
                </Box>
                <Box
                  className="box-default"
                  sx={{
                    mt: configEntzy.APP_SPACING_XL,
                  }}
                  hidden={loading || linkInProgress || alert}
                >
                  <Typography variant="h6">Generating Revenue</Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    Accept payments and generate income
                  </Typography>
                  <Typography variant="subtitle1">
                    from Eventuators and Direct Pay
                  </Typography>
                  <Typography variant="subtitle1">
                    <em>
                      Income fees are {configEntzy.FEE_VARIABLE * 100}% + $
                      {configEntzy.FEE_FIXED_USD.toFixed(2)}
                    </em>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {loading && (
          <Box
            className="box-default"
            sx={{
              mt: configEntzy.APP_SPACING_XL,
              mb: configEntzy.APP_SPACING_XL,
            }}
          >
            <ActionLoader />
          </Box>
        )}
        {linkInProgress && (
          <Box
            className="box-default"
            sx={{ mt: configEntzy.APP_SPACING_MD2X }}
          >
            <ActionAlert
              severity="info"
              title="Bank Link in Progress"
              message="Tap here to refresh when you have completed the connection with Stripe"
              onClick={handleRefresh}
            />
          </Box>
        )}
        {alert && message && (
          <Box
            className="box-default"
            sx={{ mt: configEntzy.APP_SPACING_MD2X }}
          >
            <ActionAlert severity="error" message={message} />
            {!context.state.user.status.payout_set && (
              <Box className="box-default">
                <Box
                  className="box-default"
                  sx={{ mt: configEntzy.APP_SPACING_MD2X }}
                >
                  <Container maxWidth="xs">
                    <Typography variant="subtitle2">
                      If you entered your data successfully over at Stripe and
                      only the final linking step failed here with Entzy, you
                      can retry this link validation below. If it still fails,
                      the account is missing some data. Try a fresh connection
                      above or contact us for support.
                    </Typography>
                  </Container>
                </Box>
                <Box
                  className="box-default"
                  sx={{ mt: configEntzy.APP_SPACING_MD2X }}
                >
                  <ActionBoxButton
                    variant="contained"
                    bgColor="black"
                    color="white"
                    borderColor="purple"
                    size="medium"
                    text="Try the account link again"
                    subtext="Revalidate the data you entered at Stripe"
                    onClick={() => {
                      setMessage("");
                      setAlert(false);
                      setSuccess(false);
                      navigate("/settings/income/bank/validate");
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
        {success && message && (
          <Box className="box-default" sx={{ mt: configEntzy.APP_SPACING_XL }}>
            <ActionAlert severity="success" message={message} />
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default IncomeBank;

// + '&stripe_user[business_type]=individual'
// + '&suggested_capabilities[]=transfers'
// ---------------
// settings.payouts.schedule.interval
// string
// How frequently funds will be paid out
// One of manual (payouts only created via API call), daily, weekly, or monthly.
// after account is created update settings? https://stripe.com/docs/api/accounts/update
// ---------------
